import { useEffect, useState } from 'react';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export type ThemeMode = 'light' | 'dark' | 'system';
export type ResolvedTheme = 'light' | 'dark';

interface ThemeModeState {
  mode: ThemeMode;
  setMode: (mode: ThemeMode) => void;
}

/**
 * The user's *stated* preference, persisted per app.
 *
 * The storage key is namespaced by app (see `createThemeModeStore`) so that
 * signing into admin and platform in the same browser doesn't have one app
 * overwrite the other's preference.
 */
export const useThemeModeStore = create<ThemeModeState>()(
  persist(
    (set) => ({
      mode: 'system',
      setMode: (mode) => set({ mode }),
    }),
    { name: 'opengoal.theme-mode' },
  ),
);

const DARK_QUERY = '(prefers-color-scheme: dark)';

function getSystemTheme(): ResolvedTheme {
  if (typeof window === 'undefined' || !window.matchMedia) return 'light';
  return window.matchMedia(DARK_QUERY).matches ? 'dark' : 'light';
}

/**
 * The theme actually being rendered.
 *
 * Separate from the stored mode because "system" is not a theme — it's a
 * subscription to the OS, and it has to re-render when the OS flips at sunset.
 */
export function useResolvedTheme(): ResolvedTheme {
  const mode = useThemeModeStore((state) => state.mode);
  const [systemTheme, setSystemTheme] = useState<ResolvedTheme>(getSystemTheme);

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;

    const query = window.matchMedia(DARK_QUERY);
    const onChange = (event: MediaQueryListEvent) => {
      setSystemTheme(event.matches ? 'dark' : 'light');
    };

    query.addEventListener('change', onChange);
    return () => query.removeEventListener('change', onChange);
  }, []);

  return mode === 'system' ? systemTheme : mode;
}

export function useThemeMode() {
  const mode = useThemeModeStore((state) => state.mode);
  const setMode = useThemeModeStore((state) => state.setMode);
  const resolved = useResolvedTheme();

  return {
    mode,
    resolved,
    setMode,
    toggle: () => setMode(resolved === 'dark' ? 'light' : 'dark'),
  };
}
